import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth.js'

const routes = [
  {
    path: '/',
    name: 'Home',
    component: () => import('@/views/Home.vue')
  },
  {
    path: '/search',
    name: 'Search',
    component: () => import('@/views/Search.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/playlists',
    name: 'PlaylistList',
    component: () => import('@/views/Playlist/PlaylistList.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/playlists/:id',
    name: 'PlaylistDetail',
    component: () => import('@/views/PlaylistDetail.vue')
    // 인증 불필요 - 공개 플레이리스트는 누구나 볼 수 있음 (Google 크롤링 지원)
    // 비공개 플레이리스트는 백엔드에서 접근 제한
  },
  {
    path: '/playlists/:id/edit',
    name: 'PlaylistEdit',
    component: () => import('@/views/Playlist/PlaylistEdit.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/concerts',
    name: 'Concerts',
    component: () => import('@/views/Concerts.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/concerts/:id',
    name: 'ConcertDetail',
    component: () => import('@/views/Concert/ConcertDetail.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/practice',
    name: 'PracticeMode',
    component: () => import('@/views/Practice/PracticeMode.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/profile',
    name: 'Profile',
    component: () => import('@/views/User/Profile.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/auth/callback',
    name: 'AuthCallback',
    component: () => import('@/views/AuthCallback.vue')
  },
  {
    path: '/shared/playlist/:token',
    name: 'SharedPlaylist',
    component: () => import('@/views/SharedPlaylist.vue')
    // 인증이 필요하지 않음 - 누구나 공유 링크로 접근 가능
  },
  {
    path: '/fanchant-archive',
    name: 'FanchantArchive',
    component: () => import('@/views/FanchantArchive.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/community/playlists',
    name: 'CommunityPlaylists',
    component: () => import('@/views/Community/PlaylistFeed.vue')
    // 인증 불필요 - 공개 플레이리스트 목록은 누구나 볼 수 있음
  },
  {
    path: '/bookmarks',
    name: 'Bookmarks',
    component: () => import('@/views/Bookmarks.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/announcements',
    name: 'Announcements',
    component: () => import('@/views/Announcements.vue')
    // 인증 불필요 - 누구나 접근 가능
  },
  {
    path: '/announcements/:id',
    name: 'AnnouncementDetail',
    component: () => import('@/views/AnnouncementDetail.vue')
    // 인증 불필요 - 누구나 접근 가능
  },
  {
    path: '/terms-of-service',
    name: 'TermsOfService',
    component: () => import('@/views/TermsOfService.vue')
    // 인증 불필요 - 누구나 접근 가능
  },
  {
    path: '/privacy-policy',
    name: 'PrivacyPolicy',
    component: () => import('@/views/PrivacyPolicy.vue')
    // 인증 불필요 - 누구나 접근 가능
  },
  // 관리자 라우터
  {
    path: '/admin/login',
    name: 'AdminLogin',
    component: () => import('@/views/Admin/AdminLogin.vue')
    // 인증 불필요 - 관리자 로그인 페이지
  },
  {
    path: '/admin',
    name: 'AdminDashboard',
    component: () => import('@/views/Admin/Dashboard.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/stats',
    name: 'AdminStats',
    component: () => import('@/views/Admin/Stats.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/users',
    name: 'AdminUsers',
    component: () => import('@/views/Admin/Users.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/playlists',
    name: 'AdminPlaylists',
    component: () => import('@/views/Admin/Playlists.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/reports',
    name: 'AdminReports',
    component: () => import('@/views/Admin/Reports.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/announcements',
    name: 'AdminAnnouncements',
    component: () => import('@/views/Admin/Announcements.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/concerts',
    name: 'AdminConcerts',
    component: () => import('@/views/Admin/Concerts.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/settings',
    name: 'AdminSettings',
    component: () => import('@/views/Admin/Settings.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/login',
    redirect: '/'
  },
  {
    path: '/admin/lyrics-test',
    name: 'AdminLyricsTest',
    component: () => import('@/views/LyricsTest.vue'),
    meta: { requiresAuth: true, requiresAdmin: false } // 일반 사용자도 테스트 가능하도록 변경
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    redirect: '/'
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// 인증 가드
router.beforeEach(async (to, from, next) => {
  console.log(`🔀 라우터 가드: ${from.path} → ${to.path}`)

  const authStore = useAuthStore()
  console.log(`🔐 인증 상태: ${authStore.isLoggedIn}, 사용자: ${authStore.user?.name || 'null'}`)

  // 인증이 필요한 페이지인지 확인
  if (to.meta.requiresAuth) {
    console.log(`🔒 인증 필요 페이지: ${to.path}`)

    // 이미 홈페이지에서 홈페이지로 리다이렉트하려는 경우 무한루프 방지
    if (to.path === '/' && from.path === '/') {
      console.log('⚠️ 홈→홈 리다이렉트 방지')
      next()
      return
    }

    // 홈페이지는 인증 불필요하므로 통과
    if (to.path === '/') {
      console.log('🏠 홈페이지 접근 - 인증 불필요')
      next()
      return
    }

    // authStore 초기화가 완료될 때까지 잠시 대기 (게스트 모드 고려)
    await new Promise(resolve => setTimeout(resolve, 50))

    console.log(`🔐 대기 후 인증 상태: ${authStore.isLoggedIn}`)

    // 로그인되어 있지 않으면 홈으로 리다이렉트
    if (!authStore.isLoggedIn) {
      console.log(`❌ 인증 실패 - 홈으로 리다이렉트: ${to.path}`)
      // 관리자 페이지는 관리자 로그인으로 리다이렉트
      if (to.meta.requiresAdmin) {
        next('/admin/login')
      } else {
        next('/')
      }
      return
    }

    // 관리자 권한이 필요한 페이지인지 확인
    if (to.meta.requiresAdmin) {
      console.log(`🔐 관리자 권한 필요 페이지: ${to.path}`)
      const userRole = authStore.user?.role || 'user'

      if (userRole !== 'admin' && userRole !== 'super_admin') {
        console.log(`❌ 관리자 권한 없음 - 홈으로 리다이렉트 (현재 역할: ${userRole})`)
        alert('관리자 권한이 필요합니다.')
        next('/')
        return
      }

      console.log(`✅ 관리자 권한 확인 - 페이지 진입 허용: ${to.path} (역할: ${userRole})`)
    }

    console.log(`✅ 인증 성공 - 페이지 진입 허용: ${to.path}`)
  } else {
    console.log(`🆓 인증 불필요 페이지: ${to.path}`)
  }

  next()
})

export default router